import { Component, signal } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService, EncryptedContactIdRouteInterceptor, LoginEmailService, SendPasswordlessLoginEmailUserError } from '@aiwyn/auth';
import { AssetUriService, ConfigService, LaunchDarklyFlag, LaunchDarklyService } from '@aiwyn/config';
import { ROUTE_TOKEN_LOGIN, ROUTE_TOKEN_MAIN_BASE } from '../../../routing-constants';
import { BehaviorSubject, EMPTY, Observable } from 'rxjs';
import { catchError, filter, finalize, map, switchMap, take, tap } from 'rxjs/operators';
import { canShowValidationErrors, EMAIL_REGEX, FunctionNames, LoadableHelper, LoadableStatus, Logger } from '@aiwyn/shared';
import { AppLoginComponent } from '../login-components/app-login/app-login.component';

@Component({
  selector: 'app-reset-password',
  templateUrl: './reset-password.component.html',
  styleUrls: ['./reset-password.component.scss'],
  standalone: false,
})
export class ResetPasswordComponent {
  readonly canShowValidationErrors = canShowValidationErrors;
  private originalDecodedRedirectUrl: string;
  protected lockedEmailIsValid = true;

  // email: string = this.authService.user?.firebaseUser?.email || this.authService.authEmail;
  resetPasswordForm = new UntypedFormGroup({ email: new UntypedFormControl('', [Validators.required, Validators.email]) });
  isSubmissionProcessing$ = new BehaviorSubject<boolean>(false);

  isSignInProcessing$ = new BehaviorSubject<boolean>(false);

  enableMagicLinkExperience$ = this.launchDarklyService.getFeatureFlag(LaunchDarklyFlag.ENABLE_PASSWORDLESS_LOGIN);

  userEmailError = signal(false);

  constructor(
    public launchDarklyService: LaunchDarklyService,
    public authService: AuthService,
    public route: ActivatedRoute,
    public assetUriService: AssetUriService,
    private router: Router,
    private readonly logger: Logger,
    private readonly configService: ConfigService,
    private readonly encryptedContactId: EncryptedContactIdRouteInterceptor,
    private readonly loginEmailService: LoginEmailService,
  ) {
    this.authService.refreshToken();

    this.encryptedContactId.getDecryptedContactId().pipe(
      filter(({ status }) => LoadableHelper.isLoaded(status, true)),
      tap(({ data: decryptedData }) => {
        if (decryptedData) {
          this.resetPasswordForm.get('email').setValue(decryptedData.email);
          this.resetPasswordForm.get('email').disable();
        }
      }),
      take(1),
    ).subscribe();
  }

  goBack(): void {
    this.authService.getUser(true).pipe(
      take(1),
      switchMap(user => this.router.navigate(user ? [ROUTE_TOKEN_MAIN_BASE, 'accountSettings'] : [ROUTE_TOKEN_LOGIN])),
    ).subscribe();
  }

  submit(): void {
    this.resetPasswordForm.markAllAsTouched();
    if (this.resetPasswordForm.invalid) {
      this.logger.debug('AUTH : ResetPasswordComponent : submit : Form invalid. Cannot submit.');
      return;
    }

    this.isSubmissionProcessing$.next(true);

    this.authService.requestPasswordReset(this.resetPasswordForm.controls.email.value).pipe(
      take(1),
      finalize(() => this.isSubmissionProcessing$.next(false)),
    ).subscribe();
  }

  showGuestLogin(): boolean {
    return !this.configService.relaxedModeEnabled && !this.configService.bypassClientKey;
  }

  getClientKeyForAnonymousUser(): void {
    this.router.navigateByUrl('clientkey');
  }

  validate(callingFunction: FunctionNames<AppLoginComponent>): Observable<boolean> {
    this.lockedEmailIsValid = true;
    this.resetPasswordForm.markAllAsTouched();

    return this.encryptedContactId.getDecryptedContactId().pipe(
      filter(({ status }) => LoadableHelper.isLoaded(status, true)),
      map(({ status, data: decryptedData }) => {
        if (status !== LoadableStatus.ERROR && decryptedData && (this.resetPasswordForm.get('email').value as string).match(EMAIL_REGEX)) {
          return true;
        }

        if (this.resetPasswordForm.invalid) {
          this.logger.debug(`AppLoginComponent : ${callingFunction} : Form is not valid`);
        }

        // There can be a strange case where we disable the email control, and the email isn't valid
        // In this case, the form is neither valid or invalid, so we need to check for whether the email
        // is valid or not
        if (!this.resetPasswordForm.invalid && !this.resetPasswordForm.valid) {
          const validatorResults = Validators.email(this.resetPasswordForm.controls.email);
          if (validatorResults) {
            this.lockedEmailIsValid = false;
            this.logger.debug(`AppLoginComponent : ${callingFunction} : Email is not valid`);
            return false;
          }
        }

        // If every control in a form is disabled, the form's status is disabled, which results in .valid = false.
        // The second OR condition checks the one case where the form is disabled but still valid.
        return this.resetPasswordForm.valid || EMAIL_REGEX.test(this.resetPasswordForm.value.email);
      }),
      take(1),
    );
  }

  signInWithEmailLink(): void {
    this.userEmailError.set(false);

    this.validate('signInWithEmailLink').pipe(
      take(1),
      switchMap(isValid => {
        if (!isValid) {
          return EMPTY;
        }

        this.isSignInProcessing$.next(true);
        return this.loginEmailService.sendPasswordlessLoginEmail(this.resetPasswordForm.controls.email.value).pipe(
          take(1),
          catchError(error => {
            if (error instanceof SendPasswordlessLoginEmailUserError) {
              this.userEmailError.set(true);
              return EMPTY;
            }

            throw error;
          }),
          finalize(() => this.isSignInProcessing$.next(false)),
        );
      }),
    ).subscribe();
  }
}
